import { Capacitor } from '@capacitor/core';
import { getPremiumSubscriber } from '../stores/adFreeSubscriberStore';
import {
  AdMob,
  RewardAdPluginEvents,
  AdMobRewardItem,
  BannerAdOptions,
  BannerAdSize,
  BannerAdPosition,
  BannerAdPluginEvents,
  InterstitialAdPluginEvents,
  AdOptions,
  AdmobConsentStatus,
} from '@capacitor-community/admob';

// PrivacyOptionsRequirementStatus isn't re-exported from the package's public
// entry point (gap in v8.0.0), so compare against its string literal values.
const PRIVACY_OPTIONS_REQUIRED = 'REQUIRED';

// ─── Ad Unit IDs ────────────────────────────────────────────────────────────
// Replace these with your real IDs from https://admob.google.com
// Use TEST IDs while developing to avoid policy violations.

const AD_IDS = {
  android: {
    // Shikhun Italian Android AdMob IDs
    app:          'ca-app-pub-7754802589108768~8491699966', // Android App ID
    rewarded:     'ca-app-pub-7754802589108768/1765501328', // Rewarded Android
    banner:       'ca-app-pub-7754802589108768/1402539249', // Banner Android
    interstitial: 'ca-app-pub-7754802589108768/5368014340', // Interstitial Android
  },
  ios: {
    app:          'ca-app-pub-7754802589108768~8471043303', // Shikhun Italian iOS App ID
    rewarded:     'ca-app-pub-7754802589108768/1259243051', // Shikhun Rewarded unit
    banner:       'ca-app-pub-7754802589108768/9811686344', // Shikhun Banner unit
    interstitial: 'ca-app-pub-7754802589108768/9289013258', // Interstitial iOS unit
  },
};

// ─── Platform helpers ────────────────────────────────────────────────────────

export function isNativeMobile(): boolean {
  return Capacitor.isNativePlatform();
}

function getAdUnitId(type: 'rewarded' | 'banner' | 'interstitial'): string {
  const platform = Capacitor.getPlatform(); // 'android' | 'ios' | 'web'
  if (platform === 'android') return AD_IDS.android[type];
  if (platform === 'ios') return AD_IDS.ios[type];
  return ''; // web — handled by AdSense
}

// ─── UMP / GDPR Consent (CMP) ────────────────────────────────────────────────
// AdMob policy requires a certified Consent Management Platform for users in
// the EEA, UK, and Switzerland. The Google User Messaging Platform (UMP) SDK
// bundled with this plugin acts as that CMP.

let privacyOptionsRequired = false;

/** Whether the user must be shown an ongoing way to change their ad-consent choice. */
export function isAdPrivacyOptionsRequired(): boolean {
  return privacyOptionsRequired;
}

/**
 * Requests up-to-date consent info and, if the user is in a region requiring
 * consent (EEA/UK/Switzerland) and hasn't decided yet, shows Google's consent
 * form. Must complete before any ad request is made.
 */
async function requestAdConsent(): Promise<void> {
  try {
    const consentInfo = await AdMob.requestConsentInfo();

    if (
      consentInfo.isConsentFormAvailable &&
      consentInfo.status === AdmobConsentStatus.REQUIRED
    ) {
      const updated = await AdMob.showConsentForm();
      privacyOptionsRequired =
        updated.privacyOptionsRequirementStatus === PRIVACY_OPTIONS_REQUIRED;
    } else {
      privacyOptionsRequired =
        consentInfo.privacyOptionsRequirementStatus === PRIVACY_OPTIONS_REQUIRED;
    }
  } catch (e) {
    console.warn('AdMob consent error:', e);
  }
}

/** Lets the user revisit/change their ad-consent choice (e.g. from Settings). */
export async function showAdPrivacyOptions(): Promise<void> {
  if (!isNativeMobile()) return;
  try {
    await AdMob.showPrivacyOptionsForm();
  } catch (e) {
    console.warn('AdMob privacy options error:', e);
  }
}

// ─── Banner size tracking ────────────────────────────────────────────────────
// ADAPTIVE_BANNER height varies by device width, so the layout can't assume a
// fixed height without risking the banner overlapping page content. This
// mirrors the real, native-reported height into a CSS variable that App.css
// uses to reserve exactly enough space.

// Google's anchored adaptive banner is 32/50/90dp depending on screen height,
// so on a tall phone it can be 90dp. The fallback must cover the tallest case:
// briefly reserving too much space is invisible, whereas reserving too little
// puts the banner on top of the header. The real height replaces this as soon
// as the first SizeChanged event lands.
const DEFAULT_BANNER_HEIGHT_PX = 90;
/** Breathing room between the banner and the header below it. */
const BANNER_GAP_PX = 28;
let bannerSizeListenerAttached = false;

function setBannerHeightVar(px: number): void {
  document.documentElement.style.setProperty('--ad-banner-height', `${px}px`);
}

/**
 * Must be attached BEFORE the first showBanner() call. The plugin emits
 * SizeChanged from onAdLoaded(), and an event fired while no listener is
 * attached is lost — leaving the CSS variable stuck on the fallback and the
 * banner overlapping page content. Hence this is called from showBannerAdAt()
 * as well as from initializeAdMob(), which awaits the consent flow first and
 * can therefore still be running when a route change asks for a banner.
 */
function attachBannerSizeListener(): void {
  if (bannerSizeListenerAttached) return;
  bannerSizeListenerAttached = true;
  setBannerHeightVar(DEFAULT_BANNER_HEIGHT_PX);
  AdMob.addListener(BannerAdPluginEvents.SizeChanged, (info) => {
    if (info?.height) {
      // Measured: the plugin reports 64dp on a 411x914dp screen. Small buffers
      // (4px, then 12px) still read as the ad resting on the header, so the
      // clearance is deliberately generous.
      setBannerHeightVar(Math.ceil(info.height) + BANNER_GAP_PX);
    }
    // height === 0 means the banner was hidden; keep the last real height so
    // the reserved space doesn't collapse and re-jump on the next screen.
  });
}

// ─── Initialization ──────────────────────────────────────────────────────────

let initialized = false;
let initPromise: Promise<void> | null = null;

export async function initializeAdMob(): Promise<void> {
  if (!isNativeMobile()) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      // Attach listeners before anything can emit — consent below is slow and
      // a route change during it can request a banner.
      attachBannerSizeListener();
      attachInterstitialListeners();
      await requestAdConsent();
      await AdMob.initialize({
        initializeForTesting: false, // set true while using test ad unit IDs
      });
      initialized = true;
      preloadInterstitialAd();
      console.log('✅ AdMob initialized');
    } catch (e) {
      console.warn('AdMob init error:', e);
    }
  })();

  return initPromise;
}

/**
 * Guards every ad call. AdMob.initialize() is what assigns the plugin's
 * mViewGroup (BannerExecutor.initialize()); calling showBanner() before it
 * lands dereferences null and kills the app with:
 *
 *   NullPointerException: ... ViewGroup.addView(...) on a null object reference
 *       at BannerExecutor.lambda$createNewAdView$5
 *
 * initializeAdMob() awaits the consent flow first, which can take seconds and
 * may present a form, so this window is wide and a user navigating early lands
 * squarely in it. Awaiting the shared promise closes it.
 */
async function ensureAdMobReady(): Promise<boolean> {
  if (!isNativeMobile()) return false;
  await initializeAdMob();
  return initialized;
}

// ─── Rewarded Ad ─────────────────────────────────────────────────────────────

/**
 * Shows a rewarded ad. Calls onRewarded() when the user earns the reward.
 * Calls onFailed() if the ad could not be shown.
 * On web (non-native), falls through — caller handles the fallback.
 */
export async function showRewardedAd(
  onRewarded: () => void,
  onFailed?: () => void
): Promise<void> {
  if (getPremiumSubscriber()) {
    onRewarded();
    return;
  }
  if (!isNativeMobile()) {
    // Web: no native rewarded ad — caller shows countdown simulation
    onFailed?.();
    return;
  }

  // Don't touch the SDK before AdMob.initialize() has resolved.
  if (!(await ensureAdMobReady())) {
    onFailed?.();
    return;
  }

  const adUnitId = getAdUnitId('rewarded');

  return new Promise((resolve) => {
    // Listen for reward event
    const rewardedListener = AdMob.addListener(
      RewardAdPluginEvents.Rewarded,
      (reward: AdMobRewardItem) => {
        console.log('✅ Ad reward earned:', reward);
        onRewarded();
        rewardedListener.then(l => l.remove());
        resolve();
      }
    );

    // Listen for failure
    const failedListener = AdMob.addListener(
      RewardAdPluginEvents.FailedToLoad,
      () => {
        console.warn('⚠️ Rewarded ad failed to load');
        onFailed?.();
        failedListener.then(l => l.remove());
        resolve();
      }
    );

    const options: AdOptions = { adId: adUnitId };

    AdMob.prepareRewardVideoAd(options)
      .then(() => AdMob.showRewardVideoAd())
      .catch((err: unknown) => {
        console.warn('AdMob rewarded error:', err);
        onFailed?.();
        resolve();
      });
  });
}

// ─── Interstitial Ad ─────────────────────────────────────────────────────────
// Shown at natural break points (lesson complete, quiz complete) — never on
// app launch, per AdMob policy.

let interstitialReady = false;
let interstitialLoading = false;

function preloadInterstitialAd(): void {
  if (!isNativeMobile() || interstitialLoading) return;
  interstitialLoading = true;
  AdMob.prepareInterstitial({ adId: getAdUnitId('interstitial') })
    .then(() => {
      interstitialReady = true;
      interstitialLoading = false;
    })
    .catch((e: unknown) => {
      console.warn('AdMob interstitial preload error:', e);
      interstitialReady = false;
      interstitialLoading = false;
    });
}

function attachInterstitialListeners(): void {
  AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
    interstitialReady = false;
    preloadInterstitialAd(); // preload the next one immediately
  });
  AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => {
    interstitialReady = false;
    preloadInterstitialAd();
  });
}

/**
 * Shows the preloaded interstitial ad, if one is ready. Silently no-ops
 * (no-op for premium subscribers and on web) — callers don't need a fallback,
 * this is a bonus ad slot, not a gate.
 */
export async function showInterstitialAd(): Promise<void> {
  if (!isNativeMobile() || getPremiumSubscriber() || !interstitialReady) return;
  interstitialReady = false;
  try {
    await AdMob.showInterstitial();
  } catch (e) {
    console.warn('AdMob interstitial show error:', e);
    preloadInterstitialAd();
  }
}

// ─── Banner Ad ───────────────────────────────────────────────────────────────

/**
 * Serialises banner work. Concurrent showBanner / removeBanner calls crash the
 * plugin, but *dropping* the second call is not safe either: BannerAdManager
 * flips its `bannerVisible` ref before calling, so a discarded call leaves that
 * ref describing a state the native side never reached — and the banner then
 * lingers on screens that must not have one. Chaining keeps the two in step.
 */
let bannerQueue: Promise<void> = Promise.resolve();

function enqueueBannerOp(op: () => Promise<void>): Promise<void> {
  bannerQueue = bannerQueue.then(op, op);
  return bannerQueue;
}

/**
 * Shows a native AdMob banner pinned to the top of the screen.
 * Managed globally by BannerAdManager — do not call from individual pages.
 */
async function showBannerAdAt(position: BannerAdPosition): Promise<void> {
  if (!isNativeMobile()) return;
  // Attach before the first show so onAdLoaded's SizeChanged is never missed.
  attachBannerSizeListener();
  return enqueueBannerOp(async () => {
    // Never call showBanner before AdMob.initialize() has assigned mViewGroup.
    if (!(await ensureAdMobReady())) return;
    try {
      const options: BannerAdOptions = {
        adId: getAdUnitId('banner'),
        adSize: BannerAdSize.ADAPTIVE_BANNER,
        position,
        margin: 0,
        isTesting: false,
      };
      await AdMob.showBanner(options);
    } catch (e) {
      console.warn('AdMob banner error:', e);
    }
  });
}

/**
 * Shows a native AdMob banner pinned to the top of the screen.
 */
export async function showBannerAd(): Promise<void> {
  return showBannerAdAt(BannerAdPosition.TOP_CENTER);
}

/**
 * Alias for showBannerAd() — top-pinned banner.
 */
export async function showBannerAdTop(): Promise<void> {
  return showBannerAdAt(BannerAdPosition.TOP_CENTER);
}

export async function hideBannerAd(): Promise<void> {
  if (!isNativeMobile()) return;
  return enqueueBannerOp(async () => {
    // If init never completed there is no banner to remove, and removeBanner
    // touches the same null mViewGroup.
    if (!initialized) return;
    try {
      await AdMob.removeBanner();
    } catch (e) {
      console.warn('AdMob remove banner error:', e);
    }
  });
}
